import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { SceneryService } from 'src/app/services/scenery.service';
import { AppState } from 'src/app/store/app.reducer';
import * as SceneryActions from './scenery.actions';
import * as NotificationsActions from '../notifications/notifications.actions';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';

@Injectable()
export class SceneryEffects {
    getSceneries$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.getSceneries),
        switchMap((action) => this.sceneryService.getSceneries(null, action.siteId).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.getSceneriesSuccess({ sceneries: resp.Data });
                    } else {
                        return SceneryActions.getSceneriesFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.getSceneriesFailed({ errorMessage: error.message })))
            )
        )
    ));

    editScenery$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.editScenery),
        switchMap((action) => this.sceneryService.editScenery(action.scenery).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.editScenerySuccess({
                            message: this.translate.instant('SCENARIO_EDIT_SUCCESS'),
                            scenery: action.scenery });
                    } else {
                        return SceneryActions.editSceneryFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.editSceneryFailed({ errorMessage: error.message })))
            )
        )
    ));

    editScenerySuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.editScenerySuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'edit_scenary_completed', params: { page_title: 'Scenarios | Scenaries' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    executeScenery$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.executeScenery),
        switchMap((action) => this.sceneryService.executeScenery(action.id).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.executeScenerySuccess({
                            message: this.translate.instant('EXECUTE_SCENARIO_SUCCESS'),
                            sceneryId: action.id });
                    } else {
                        return SceneryActions.executeSceneryFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.executeSceneryFailed({ errorMessage: error.message })))
            )
        )
    ));

    executeScenerySuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.executeScenerySuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'execute_scenary_completed', params: { page_title: 'Scenarios | Scenaries' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    getSteps$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.getSteps),
        switchMap((action) => this.sceneryService.getSteps(action.sceneryId).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.getStepsSuccess({ steps: resp.Data });
                    } else {
                        return SceneryActions.getStepsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.getStepsFailed({ errorMessage: error.message })))
            )
        )
    ));

    reorderSteps$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.reorderSteps),
        map(action => action.steps),
        switchMap((steps) => this.sceneryService.reorderSteps(steps)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.reorderStepsSuccess({ steps });
                    } else {
                        return SceneryActions.reorderStepsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.reorderStepsFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteStep$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.deleteStep),
        switchMap((action) => this.sceneryService.deleteStep(action.stepId)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.deleteStepSuccess({
                            message: this.translate.instant('STEP_DELETE_SUCCESS'),
                            stepId: action.stepId
                        });
                    } else {
                        return SceneryActions.deleteStepFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.deleteStepFailed({ errorMessage: error.message })))
            )
        )
    ));

    deleteStepSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.deleteStepSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'delete_step_completed', params: { page_title: 'Scenarios | Scenaries' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    getActions$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.getActions),
        switchMap((action) => this.sceneryService.getActions(action.stepId).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.getActionsSuccess({ actions: resp.Data });
                    } else {
                        return SceneryActions.getActionsFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.getActionsFailed({ errorMessage: error.message })))
            )
        )
    ));

    addScenery$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.addScenery),
        switchMap((action) => this.sceneryService.addScenery(action.scenery).pipe(
            map((resp) => {
                if (!resp.Success) {
                    return SceneryActions.addSceneryFailed({ errorMessage: 'Nasty Error' });
                }
                return SceneryActions.addScenerySuccess({
                    message: this.translate.instant('SCENARIO_ADD_SUCCESS'),
                    scenery: { ...action.scenery, Id: resp.Data.Id },
                });
            }),
            catchError(error => of(SceneryActions.addSceneryFailed({ errorMessage: error.message })))
        ))
    ));

    addScenerySuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.addScenerySuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'new_scenary_completed', params: { page_title: 'Scenarios | Scenaries' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    deleteScenery$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.deleteScenery),
        switchMap((action) => this.sceneryService.deleteScenery(action.sceneryId).pipe(
            map((resp) => {
                if (!resp.Success) {
                    return SceneryActions.deleteSceneryFailed({ errorMessage: 'Nasty Error' });
                }
                return SceneryActions.deleteScenerySuccess({
                    message: this.translate.instant('SCENARIO_DELETE_SUCCESS'),
                    sceneryId: action.sceneryId
                });
            }),
            catchError(error => of(SceneryActions.deleteSceneryFailed({ errorMessage: error.message })))
        ))
    ));

    deleteScenerySuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.deleteScenerySuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'delete_scenary_completed', params: { page_title: 'Scenarios | Scenaries' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    addStep$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.addStep),
        withLatestFrom(
            this.store.select('scenery', 'steps'),
        ),
        switchMap(([action, steps]) => {
            const newStep = {...action.step, Index: steps.filter(step => step.SceneryId === action.step.SceneryId).length};
            return this.sceneryService.addStep(newStep).pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.addStepSuccess({
                            message: this.translate.instant('STEP_ADD_SUCCESS'),
                            step: { ...newStep, Id: resp.Data.Id },
                            action: { ...action.action, StepId: resp.Data.Id }
                        });
                    } else {
                        return SceneryActions.addStepFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.addStepFailed({ errorMessage: error.message })))
            );
        })
    ));

    addStepSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.addStepSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'new_step_completed', params: { page_title: 'Scenarios | Scenaries' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    editStep$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.editStep),
        switchMap((action) => this.sceneryService.editStep(action.step)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.editStepSuccess({
                            message: this.translate.instant('STEP_EDIT_SUCCESS'),
                            step: action.step,
                            action: action.action
                        });
                    } else {
                        return SceneryActions.editStepFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.editStepFailed({ errorMessage: error.message })))
            )
        )
    ));

    editStepSuccessAnalytics$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.editStepSuccess),
        tap(() => {
            setTimeout(() => {
                this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'edit_step_completed', params: { page_title: 'Scenarios | Scenaries' } }));
            }, 1000);
        }),
    ), { dispatch: false });

    addAction$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.addStepSuccess, SceneryActions.addAction),
        map(action => action.action),
        switchMap((action) => this.sceneryService.addAction(action)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.addActionSuccess({ action: { ...action, Id: resp.Data.Id } });
                    } else {
                        return SceneryActions.addActionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.addActionFailed({ errorMessage: error.message })))
            )
        )
    ));

    editAction$ = createEffect(() => this.actions$.pipe(
        ofType(SceneryActions.editStepSuccess, SceneryActions.editAction),
        map(action => action.action),
        switchMap((action) => this.sceneryService.editAction(action)
            .pipe(
                map((resp) => {
                    if (resp.Success) {
                        return SceneryActions.editActionSuccess({ action });
                    } else {
                        return SceneryActions.editActionFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SceneryActions.editActionFailed({ errorMessage: error.message })))
            )
        )
    ));

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            SceneryActions.addScenerySuccess,
            SceneryActions.editScenerySuccess,
            SceneryActions.deleteScenerySuccess,
            SceneryActions.addStepSuccess,
            SceneryActions.editStepSuccess,
            SceneryActions.deleteStepSuccess,
            SceneryActions.executeScenerySuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("SCENERY", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private sceneryService: SceneryService,
        private translate: TranslateService,
    ) { }
}
